import { useCallback, useRef, useState } from 'react';

/**
 * The talking half of visai, without any of its chrome.
 *
 * Pulled out of the view so the floating widget and anything later can share
 * one implementation of the streaming protocol — the part with the fiddly
 * edges (events split across chunks, an empty bubble to clean up on error) —
 * rather than each growing its own slightly-wrong copy.
 */

/** A chart the backend built from a tool's own figures — never from the model. */
export type ChartSpec =
  | {
      kind: 'timeseries';
      title: string;
      bucket: 'hour' | 'day';
      buckets: Array<{ t: string; good: number; unsure: number; bad: number }>;
    }
  | {
      kind: 'bars';
      title: string;
      unit: string;
      rows: Array<{ label: string; value: number; sub?: string }>;
    };

export type Turn = { role: 'user' | 'assistant'; content: string; charts?: ChartSpec[] };

export interface VisaiChat {
  turns: Turn[];
  activity: string[];
  streaming: boolean;
  error: string | null;
  ask: (question: string) => Promise<void>;
  reset: () => void;
}

/** What each tool is really doing, in the words someone here would use. */
const TOOL_LABELS: Record<string, string> = {
  list_scope: 'looking up the lines and products',
  quality_summary: 'reading the figures for that period',
  quality_timeseries: 'following it through the period',
  top_defects: 'checking which defects were recorded',
  compare_stations: 'comparing the lines',
  compare_customers: 'comparing the customers',
};

export function useVisaiChat(): VisaiChat {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [activity, setActivity] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  // The history the backend needs — kept in a ref so a rapid second question
  // reads the turns as they are now, not as they were when `ask` was created.
  const turnsRef = useRef<Turn[]>([]);

  const reset = useCallback(() => {
    setTurns([]);
    turnsRef.current = [];
    setActivity([]);
    setError(null);
  }, []);

  const ask = useCallback(
    async (question: string) => {
      const text = question.trim();
      if (!text || streaming) return;

      // The whole history goes up every time — the backend holds no session, so
      // a reload is a genuinely new conversation rather than a half-remembered
      // one resumed.
      const history = [...turnsRef.current, { role: 'user' as const, content: text }];
      turnsRef.current = history;
      setTurns([...history, { role: 'assistant', content: '' }]);
      setError(null);
      setActivity([]);
      setStreaming(true);

      try {
        const res = await fetch('/api/chat', {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ messages: history }),
        });

        if (!res.ok || !res.body) {
          throw new Error(
            res.status === 503
              ? 'visai is not switched on here — an API key has to be configured first.'
              : `Request failed (${res.status})`,
          );
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let answer = '';
        const charts: ChartSpec[] = [];

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // Events are separated by a blank line, and a chunk can split one in
          // half — so only whole events are consumed and the remainder waits.
          const parts = buffer.split('\n\n');
          buffer = parts.pop() ?? '';

          for (const part of parts) {
            const line = part.split('\n').find((l) => l.startsWith('data: '));
            if (!line) continue;
            const event = JSON.parse(line.slice(6)) as
              | { type: 'text'; text: string }
              | { type: 'tool'; name: string }
              | { type: 'chart'; spec: ChartSpec }
              | { type: 'error'; message: string }
              | { type: 'done' };

            if (event.type === 'text') {
              answer += event.text;
              setTurns((t) => {
                const next = [...t];
                const last = next[next.length - 1];
                if (last?.role === 'assistant') last.content = answer;
                return next;
              });
            } else if (event.type === 'tool') {
              setActivity((a) => [...a, TOOL_LABELS[event.name] ?? event.name]);
            } else if (event.type === 'chart') {
              // Charts arrive as the tools return — usually before the words.
              // Attach to the open assistant turn so they render with it.
              charts.push(event.spec);
              setTurns((t) => {
                const next = [...t];
                const last = next[next.length - 1];
                if (last?.role === 'assistant') last.charts = [...charts];
                return next;
              });
            } else if (event.type === 'error') {
              setError(event.message);
            }
          }
        }

        // Record the finished answer in the history the next question will send.
        turnsRef.current = [...history, { role: 'assistant', content: answer }];
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Something went wrong');
        // Drop the empty assistant bubble — an empty answer beside an error
        // reads as though visai replied with silence.
        setTurns((t) => (t[t.length - 1]?.content === '' ? t.slice(0, -1) : t));
        turnsRef.current = history;
      } finally {
        setStreaming(false);
        setActivity([]);
      }
    },
    [streaming],
  );

  return { turns, activity, streaming, error, ask, reset };
}
